import { datadogRum } from '@datadog/browser-rum'

const _CHUNK_LOAD_FAILED_ACTION = 'chunk_load_failed'

type ChunkLoadOutcome = 'reloaded' | 'guarded' | 'no_storage'

type ReloadClaim = {
  outcome: ChunkLoadOutcome
  sinceLastReloadMs?: number
}

type ChunkLoadFailureKind = 'chunk_load' | 'css_preload' | 'other'

/**
 * Recovery needs per-tab storage and detectable storage failures.
 * The shared storage helper uses localStorage and hides those failures.
 */
export const _RELOADED_AT_KEY = 'regard:shell:chunkReloadedAt'

// Allow recovery from later deploys without immediately retrying a failed reload.
const _RELOAD_GUARD_WINDOW_MS = 10_000

// Keeps the reported message readable in RUM without shipping whole stack-like messages.
const _ERROR_MESSAGE_MAX_LENGTH = 200

// Lowercase message fragments: Chrome, Firefox and Safari dynamic-import failures, then Vite's CSS preload failure.
const _CHUNK_LOAD_SIGNATURES = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
]
const _CSS_PRELOAD_SIGNATURE = 'unable to preload css'

// Storage failures block recovery because an unrecorded reload could loop.
const _claimReload = (): ReloadClaim => {
  try {
    const now = Date.now()
    const lastReloadedAt = Number(
      window.sessionStorage.getItem(_RELOADED_AT_KEY),
    )
    const sinceLastReloadMs = now - lastReloadedAt
    if (sinceLastReloadMs < _RELOAD_GUARD_WINDOW_MS) {
      return { outcome: 'guarded', sinceLastReloadMs }
    }

    window.sessionStorage.setItem(_RELOADED_AT_KEY, String(now))
    return { outcome: 'reloaded' }
  } catch {
    return { outcome: 'no_storage' }
  }
}

const _messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

const _kindOf = (message: string): ChunkLoadFailureKind => {
  const lowered = message.toLowerCase()
  if (_CHUNK_LOAD_SIGNATURES.some((signature) => lowered.includes(signature))) {
    return 'chunk_load'
  }
  return lowered.includes(_CSS_PRELOAD_SIGNATURE) ? 'css_preload' : 'other'
}

// Browsers report absolute URLs. Vite's CSS preload reports the root-relative asset path.
const _chunkUrlOf = (message: string): string | undefined =>
  /https?:\/\/\S+|\/assets\/\S+/.exec(message)?.[0]

/**
 * Loads an app chunk, reporting failures to RUM and attempting recovery with a guarded reload.
 * Rethrows when recovery is blocked.
 */
export const withChunkLoadRecovery = async <Loaded>(
  load: () => Promise<Loaded>,
  reload: () => void = () => {
    window.location.reload()
  },
): Promise<Loaded> => {
  try {
    return await load()
  } catch (error) {
    const { outcome, sinceLastReloadMs } = _claimReload()
    const message = _messageOf(error)
    const chunk = _chunkUrlOf(message)
    datadogRum.addAction(_CHUNK_LOAD_FAILED_ACTION, {
      outcome,
      kind: _kindOf(message),
      error_name: error instanceof Error ? error.name : typeof error,
      error_message: message.slice(0, _ERROR_MESSAGE_MAX_LENGTH),
      online: navigator.onLine,
      ...(chunk === undefined ? {} : { chunk }),
      ...(sinceLastReloadMs === undefined
        ? {}
        : { since_last_reload_ms: sinceLastReloadMs }),
    })

    if (outcome !== 'reloaded') {
      throw error
    }

    reload()
    // Keep the router waiting instead of flashing an error screen during the reload.
    return new Promise<never>(() => {})
  }
}
